import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { can } from '../../utils/roleUtils';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button, IconButton,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert, Tooltip,
  useMediaQuery, useTheme, Tabs, Tab, Stack, FormControl,
  InputLabel, Select, MenuItem, ToggleButton, ToggleButtonGroup, Snackbar, Link
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  SwapHoriz as TransferIcon,
} from '@mui/icons-material';
import WalletService from '../../services/wallet.service';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import IssuedCheques from './IssuedCheques';
import ReceivedCheques from './ReceivedCheques';
import CreditCards from '../accounting/CreditCards';
import BankTransferDialog from './BankTransferDialog';

const BANKING_TABS = ['bank', 'cheques', 'cards'];
const TAB_SX = { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minHeight: 40 };

const fontSize = {
  xs: '0.75rem',
  sm: '0.8125rem',
  md: '0.875rem',
};

// The bank account form with its transfer fee presets as the API expects them: an empty value means no preset, and a
// cash account never carries one.
const bankPayload = (form) => {
  const { outFeeType, outFeeValue, inFeeType, inFeeValue, ...rest } = form;
  const cash = form.accountType === 'CASH';
  const preset = (type, value) => (cash || value === '' || value === null || value === undefined
    ? { type: null, value: null } : { type, value: Number(value) });
  const out = preset(outFeeType, outFeeValue);
  const inn = preset(inFeeType, inFeeValue);
  return { ...rest, transferOutFeeType: out.type, transferOutFeeValue: out.value, transferInFeeType: inn.type, transferInFeeValue: inn.value };
};

// One fee preset: Flat (AED) or % of the amount transferred.
const FeePresetRow = ({ label, type, value, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
    <Typography sx={{ fontSize: fontSize.sm, flex: '1 1 200px' }}>{label}</Typography>
    <ToggleButtonGroup exclusive size="small" value={type} onChange={(e, v) => { if (v) onChange({ type: v }); }}
      sx={{ '& .MuiToggleButton-root': { height: 32, px: 1.5, fontSize: '0.75rem', textTransform: 'none' } }}>
      <ToggleButton value="FLAT">Flat</ToggleButton>
      <ToggleButton value="PERCENT">%</ToggleButton>
    </ToggleButtonGroup>
    <TextField size="small" type="number" value={value} placeholder="None" sx={{ width: 120 }}
      inputProps={{ min: 0, step: '0.01', ...(type === 'PERCENT' ? { max: 100 } : {}) }}
      InputProps={{ endAdornment: <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>{type === 'PERCENT' ? '%' : 'AED'}</Typography> }}
      onChange={(e) => onChange({ value: e.target.value })} />
  </Box>
);

const Banking = () => {
  // Session user for permission checks (this page receives no currentUser prop).
  const sessionUser = (() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
  })();
  const canWriteFin = can(sessionUser, 'finance', 'write');
  const canDeleteFin = can(sessionUser, 'finance', 'delete');
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The tab lives in the URL (?tab=bank|cheques|cards) so links and the sidebar open the right tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = Math.max(0, BANKING_TABS.indexOf(searchParams.get('tab')));
  const setActiveTab = (index) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', BANKING_TABS[index]);
    setSearchParams(next, { replace: true });
  };
  const [chequeView, setChequeView] = useState('issued');

  // Bank Accounts
  const [bankAccounts, setBankAccounts] = useState([]);
  const [transfer, setTransfer] = useState({ open: false, fromId: null });
  const [transferDone, setTransferDone] = useState(null);
  const [bankDialog, setBankDialog] = useState({ open: false, editMode: false, account: null });
  const [bankForm, setBankForm] = useState({ bankName: '', accountName: '', accountNumber: '', iban: '', swiftCode: '', accountType: 'OPERATIONS', outFeeType: 'FLAT', outFeeValue: '', inFeeType: 'FLAT', inFeeValue: '' });
  const needsBankIds = bankForm.accountType === 'OPERATIONS' || bankForm.accountType === 'NGENIUS_SETTLEMENT';
  const [bankSubmitting, setBankSubmitting] = useState(false);
  const [bankDialogError, setBankDialogError] = useState(null);
  const [deleteDialogError, setDeleteDialogError] = useState(null);

  // Pulls a readable message out of our ApiResponse error envelope.
  // Backend shape: { success: false, error: "...", data?: { field: msg, ... } }
  const extractErrorMessage = (err, fallback) => {
    const body = err?.response?.data;
    if (!body) return err?.message || fallback;
    // Field-level validation errors (MethodArgumentNotValidException handler)
    if (body.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
      const fieldErrors = Object.entries(body.data)
        .map(([field, msg]) => `${field}: ${msg}`)
        .join('; ');
      if (fieldErrors) return fieldErrors;
    }
    return body.error || body.message || fallback;
  };
  const [deleteDialog, setDeleteDialog] = useState({ open: false, account: null });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setBankAccounts(await WalletService.getBankAccounts());
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load bank accounts'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Bank Account Handlers ──
  const openAddBankDialog = () => {
    setBankForm({ bankName: '', accountName: '', accountNumber: '', iban: '', swiftCode: '', accountType: 'OPERATIONS', outFeeType: 'FLAT', outFeeValue: '', inFeeType: 'FLAT', inFeeValue: '' });
    setBankDialogError(null);
    setBankDialog({ open: true, editMode: false, account: null });
  };

  const openEditBankDialog = (account) => {
    setBankForm({
      bankName: account.bankName, accountName: account.accountName,
      accountNumber: account.accountNumber, iban: account.iban,
      swiftCode: account.swiftCode, accountType: account.accountType,
      outFeeType: account.transferOutFeeType || 'FLAT', outFeeValue: account.transferOutFeeValue ?? '',
      inFeeType: account.transferInFeeType || 'FLAT', inFeeValue: account.transferInFeeValue ?? '',
    });
    setBankDialogError(null);
    setBankDialog({ open: true, editMode: true, account });
  };

  const closeBankDialog = () => {
    setBankDialog({ open: false, editMode: false, account: null });
    setBankDialogError(null);
  };

  const openDeleteBankDialog = (account) => {
    setDeleteDialogError(null);
    setDeleteDialog({ open: true, account });
  };

  const closeDeleteBankDialog = () => {
    setDeleteDialog({ open: false, account: null });
    setDeleteDialogError(null);
  };

  const handleBankSubmit = async () => {
    setBankSubmitting(true);
    setBankDialogError(null);
    try {
      const payload = bankPayload(bankForm);
      if (bankDialog.editMode) {
        await WalletService.updateBankAccount(bankDialog.account.id, payload);
      } else {
        await WalletService.createBankAccount(payload);
      }
      closeBankDialog();
      fetchData();
    } catch (err) {
      const fallback = bankDialog.editMode ? 'Failed to update bank account' : 'Failed to create bank account';
      setBankDialogError(extractErrorMessage(err, fallback));
    } finally {
      setBankSubmitting(false);
    }
  };

  const handleDeleteBank = async () => {
    try {
      await WalletService.deleteBankAccount(deleteDialog.account.id);
      closeDeleteBankDialog();
      fetchData();
    } catch (err) {
      setDeleteDialogError(extractErrorMessage(err, 'Failed to delete bank account'));
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await WalletService.setDefaultBank(id);
      fetchData();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to set default bank'));
    }
  };

  // ── Tab 3: Bank Accounts ──
  const renderBankAccountsTab = () => (
    <>
      {canWriteFin && (
      <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
        <Button variant="outlined" size="small" startIcon={<TransferIcon sx={{ fontSize: 16 }} />}
          onClick={() => setTransfer({ open: true, fromId: null })} disabled={bankAccounts.filter((b) => b.isActive).length < 2}
          sx={{ fontSize: fontSize.sm }}>
          Transfer
        </Button>
        <Button variant="contained" size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={openAddBankDialog}
          sx={{ fontSize: fontSize.sm }}>
          Add Bank Account
        </Button>
      </Box>
      )}

      {isMobile ? (
        <Stack spacing={1.5}>
          {loading ? <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box> :
          bankAccounts.length === 0 ? <Paper sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No bank accounts configured</Typography></Paper> :
          bankAccounts.map((b) => (
            <Card key={b.id} variant="outlined" sx={{ opacity: b.isActive ? 1 : 0.6 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {b.isDefault && <StarIcon sx={{ fontSize: 16, color: '#ffc107' }} />}
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{b.bankName}</Typography>
                  </Box>
                  <Chip label={b.isActive ? 'Active' : 'Inactive'} size="small" color={b.isActive ? 'success' : 'default'} sx={{ fontSize: fontSize.xs, height: 20 }} />
                </Box>
                <Typography sx={{ fontSize: fontSize.xs }}>{b.accountName}</Typography>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mt: 0.5 }}>IBAN: {b.iban}</Typography>
                <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
                  {canWriteFin && b.isActive && <IconButton size="small" onClick={() => setTransfer({ open: true, fromId: b.id })}><TransferIcon sx={{ fontSize: 16 }} /></IconButton>}
                  {canWriteFin && <IconButton size="small" onClick={() => openEditBankDialog(b)}><EditIcon sx={{ fontSize: 16 }} /></IconButton>}
                  {!b.isDefault && canWriteFin && <IconButton size="small" onClick={() => handleSetDefault(b.id)}><StarBorderIcon sx={{ fontSize: 16 }} /></IconButton>}
                  {canDeleteFin && <IconButton size="small" color="error" onClick={() => openDeleteBankDialog(b)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead sx={getBrandedTableHeadSx(theme)}>
                <TableRow>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Bank Name</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Account Name</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Account Number</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>IBAN</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Type</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Balance</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Default</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : bankAccounts.length === 0 ? (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No bank accounts configured</Typography></TableCell></TableRow>
                ) : bankAccounts.map((b) => (
                  <TableRow key={b.id} hover sx={{ opacity: b.isActive ? 1 : 0.6, bgcolor: b.isDefault ? (isDark ? 'rgba(255,193,7,0.06)' : 'rgba(255,193,7,0.04)') : 'inherit' }}>
                    <TableCell sx={{ fontSize: '13px', py: 0.75 }}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <BankIcon sx={{ fontSize: 16 }} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{b.bankName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px', py: 0.75 }}>{b.accountName}</TableCell>
                    <TableCell sx={{ fontSize: '13px', py: 1, fontFamily: 'monospace' }}>{b.accountNumber}</TableCell>
                    <TableCell sx={{ fontSize: '13px', py: 1, fontFamily: 'monospace' }}>{b.iban}</TableCell>
                    <TableCell sx={{ py: 0.75 }}>
                      <Chip label={b.accountType} size="small" sx={{ fontSize: fontSize.xs, height: 22 }} />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1, fontSize: '13px', fontWeight: 600, color: Number(b.balance) < 0 ? 'error.main' : undefined }}>
                      {WalletService.formatCurrency(b.balance || 0)}
                      {Number(b.balance) < 0 && (
                        <Tooltip title="Negative balance — reconcile with the bank statement">
                          <Chip label="Negative" size="small" color="error" variant="outlined" sx={{ ml: 1, fontSize: fontSize.xs, height: 20 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.75 }}>
                      {b.isDefault ? (
                        <StarIcon sx={{ fontSize: 20, color: '#ffc107' }} />
                      ) : (
                        <Tooltip title="Set as Default">
                          <IconButton size="small" onClick={() => handleSetDefault(b.id)}>
                            <StarBorderIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.75 }}>
                      <Chip label={b.isActive ? 'Active' : 'Inactive'} size="small"
                        color={b.isActive ? 'success' : 'default'}
                        sx={{ fontSize: fontSize.xs, height: 22 }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.75 }}>
                      {canWriteFin && b.isActive && (
                        <Tooltip title="Transfer from this account">
                          <IconButton size="small" onClick={() => setTransfer({ open: true, fromId: b.id })}>
                            <TransferIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canWriteFin && (
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditBankDialog(b)}>
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDeleteFin && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => openDeleteBankDialog(b)}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </>
  );

  // Issued / Received switch, shown at the start of each cheque view's toolbar.
  const chequeViewSwitch = (
    <ToggleButtonGroup exclusive size="small" value={chequeView} onChange={(e, v) => { if (v) setChequeView(v); }}
      sx={{ '& .MuiToggleButton-root': { height: 32, px: 1.5, fontSize: '0.75rem', textTransform: 'none' },
        '& .MuiToggleButton-root.Mui-selected': { bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } } }}>
      <ToggleButton value="issued">Issued (we paid)</ToggleButton>
      <ToggleButton value="received">Received (from customers)</ToggleButton>
    </ToggleButtonGroup>
  );

  // ── Main Render ──
  return (
    <Box sx={{ pb: isMobile ? 2 : 0, width: 0, minWidth: '100%' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
        sx={{ mb: 2, minHeight: 40, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Bank Accounts" sx={TAB_SX} />
        <Tab label="Cheques" sx={TAB_SX} />
        <Tab label="Credit Cards" sx={TAB_SX} />
      </Tabs>

      {/* Tab Content */}
      {activeTab === 0 && renderBankAccountsTab()}
      {activeTab === 1 && (
        <Box>
          {chequeView === 'issued'
            ? <IssuedCheques bankAccounts={bankAccounts} canWrite={canWriteFin} viewSwitch={chequeViewSwitch} />
            : <ReceivedCheques embedded bankAccounts={bankAccounts} canWrite={canWriteFin} onChanged={fetchData} viewSwitch={chequeViewSwitch} />}
        </Box>
      )}
      {activeTab === 2 && <CreditCards embedded />}

      {/* ── Add/Edit Bank Account Dialog ── */}
      <Dialog open={bankDialog.open} onClose={closeBankDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: fontSize.md, fontWeight: 600 }}>
          {bankDialog.editMode ? 'Edit Bank Account' : 'Add Bank Account'}
        </DialogTitle>
        <DialogContent>
          {bankDialogError && (
            <Alert severity="error" sx={{ mb: 2, fontSize: fontSize.sm }} onClose={() => setBankDialogError(null)}>
              {bankDialogError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Bank Name" required
                value={bankForm.bankName} onChange={(e) => setBankForm(f => ({ ...f, bankName: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Account Name" required
                value={bankForm.accountName} onChange={(e) => setBankForm(f => ({ ...f, accountName: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Account Number" required={needsBankIds}
                helperText={needsBankIds ? ' ' : 'Optional for cash and e-wallet accounts'}
                value={bankForm.accountNumber} onChange={(e) => setBankForm(f => ({ ...f, accountNumber: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="IBAN" required={needsBankIds}
                helperText={needsBankIds ? ' ' : 'Optional for cash and e-wallet accounts'}
                value={bankForm.iban} onChange={(e) => setBankForm(f => ({ ...f, iban: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="SWIFT Code"
                value={bankForm.swiftCode} onChange={(e) => setBankForm(f => ({ ...f, swiftCode: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Account Type</InputLabel>
                <Select value={bankForm.accountType} label="Account Type"
                  onChange={(e) => setBankForm(f => ({ ...f, accountType: e.target.value }))}>
                  <MenuItem value="OPERATIONS">Operations</MenuItem>
                  <MenuItem value="NGENIUS_SETTLEMENT">N-Genius Settlement</MenuItem>
                  <MenuItem value="CASH">Cash</MenuItem>
                  <MenuItem value="E_WALLET">E-Wallet (e.g. NOQODI, eDirham)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {bankForm.accountType !== 'CASH' && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, mb: 0.5 }}>Transfer fees</Typography>
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 1 }}>
                Filled in when a transfer uses this account; you can change them on each transfer. Leave empty for none.
              </Typography>
              <FeePresetRow label="When sending from this account" type={bankForm.outFeeType} value={bankForm.outFeeValue}
                onChange={(patch) => setBankForm((f) => ({ ...f, ...(patch.type ? { outFeeType: patch.type } : {}), ...('value' in patch ? { outFeeValue: patch.value } : {}) }))} />
              <FeePresetRow label="When receiving into this account" type={bankForm.inFeeType} value={bankForm.inFeeValue}
                onChange={(patch) => setBankForm((f) => ({ ...f, ...(patch.type ? { inFeeType: patch.type } : {}), ...('value' in patch ? { inFeeValue: patch.value } : {}) }))} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeBankDialog} size="small" sx={{ fontSize: fontSize.sm }}>Cancel</Button>
          <Button variant="contained" size="small" onClick={handleBankSubmit}
            disabled={bankSubmitting || !bankForm.bankName || !bankForm.accountName || (needsBankIds && (!bankForm.accountNumber || !bankForm.iban))}
            sx={{ fontSize: fontSize.sm }}>
            {bankSubmitting ? <CircularProgress size={18} color="inherit" /> : (bankDialog.editMode ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Bank Confirm Dialog ── */}
      <Dialog open={deleteDialog.open} onClose={closeDeleteBankDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: fontSize.md, fontWeight: 600 }}>Delete Bank Account</DialogTitle>
        <DialogContent>
          {deleteDialogError && (
            <Alert severity="error" sx={{ mb: 2, fontSize: fontSize.sm }} onClose={() => setDeleteDialogError(null)}>
              {deleteDialogError}
            </Alert>
          )}
          <Typography sx={{ fontSize: fontSize.sm }}>
            Are you sure you want to delete <strong>{deleteDialog.account?.bankName}</strong> ({deleteDialog.account?.accountNumber})?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteBankDialog} size="small" sx={{ fontSize: fontSize.sm }}>Cancel</Button>
          <Button variant="contained" color="error" size="small" onClick={handleDeleteBank} sx={{ fontSize: fontSize.sm }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <BankTransferDialog open={transfer.open} accounts={bankAccounts} initialFromId={transfer.fromId}
        onClose={() => setTransfer({ open: false, fromId: null })}
        onDone={(entry) => { setTransfer({ open: false, fromId: null }); setTransferDone(entry); fetchData(); }} />
      <Snackbar open={Boolean(transferDone)} autoHideDuration={6000} onClose={() => setTransferDone(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setTransferDone(null)} sx={{ fontSize: fontSize.sm }}>
          Transfer recorded
          {transferDone?.id && (
            <> · <Link component={RouterLink} to={`/accounting/journal-entries/${transferDone.id}`}>View entry</Link></>
          )}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Banking;
